import { css } from '@emotion/react';

import { screens } from '../shared/styles';
import { type PricingSelectorVariant } from './PricingSelector.types';

export const groupStyles = (variant: PricingSelectorVariant) => css`
  display: grid;
  align-items: stretch;
  width: 100%;
  margin: 0;
  padding: ${variant === 'detailed' ? 'var(--spacing-4)' : '0'} 0 0;
  gap: ${variant === 'compact' ? 'var(--spacing-3)' : 'var(--spacing-5)'};
  grid-template-columns: minmax(0, 1fr);

  ${variant === 'compact'
    ? `
      @media (min-width: ${screens.md}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));

        &:has(> :only-child) {
          grid-template-columns: minmax(0, 1fr);
        }
      }
    `
    : ''}
`;

export const cardStyles = (selected: boolean) => css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: var(--spacing-5);
  border: 2px solid
    ${selected ? 'var(--color-primary-800)' : 'var(--border-primary)'};
  border-radius: var(--spacing-4);
  background: ${selected ? 'var(--color-gold-50)' : 'var(--surface-page)'};
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${selected
      ? 'var(--color-primary-800)'
      : 'var(--color-neutral-300)'};
  }

  &:has(input:focus-visible) {
    outline: 2px solid var(--border-focus);
    outline-offset: 2px;
  }
`;

export const nativeRadioStyles = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const radioStyles = (selected: boolean) => css`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border: 2px solid
    ${selected ? 'var(--text-primary)' : 'var(--color-neutral-300)'};
  border-radius: 999px;
  background: var(--color-base-white);
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: var(--text-primary);
    opacity: ${selected ? 1 : 0};
    transform: translate(-50%, -50%);
  }
`;

export const headerStyles = css`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  width: 100%;
  min-width: 0;
`;

export const titleBlockStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--spacing-1);
  min-width: 0;
`;

export const namePriceRowStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-3);
  width: 100%;
  min-width: 0;

  & > :first-of-type {
    flex: 1;
    min-width: 0;
  }
`;

export const planNameStyles = (variant: PricingSelectorVariant) => css`
  min-width: 0;
  margin: 0;
  color: var(--text-primary);
  font-size: ${variant === 'detailed' ? '18px' : '16px'};
  font-weight: 700;
  line-height: 24px;
`;

export const descriptionStyles = css`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const pinnedBadgeStyles = css`
  position: absolute;
  top: 0;
  right: var(--spacing-5);
  z-index: 1;
  transform: translateY(-50%);
`;

export const priceBlockStyles = css`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

export const priceAmountStyles = (variant: PricingSelectorVariant) => css`
  display: inline-flex;
  align-items: baseline;
  margin: 0;
  color: var(--text-primary);
  font-size: ${variant === 'detailed' ? '22px' : '18px'};
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`;

export const pricePeriodStyles = css`
  margin-left: 1px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`;

export const monthlyStyles = css`
  margin: var(--spacing-1) 0 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
`;

export const dividerStyles = css`
  width: 100%;
  height: 0;
  margin: var(--spacing-4) 0;
  border: 0;
  border-top: 1px solid var(--border-primary);
`;

export const featuresHeadingStyles = css`
  margin: 0 0 var(--spacing-3);
  color: var(--color-hunter-600);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 16px;
  text-transform: uppercase;
`;

export const featureListStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const featureItemStyles = css`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-2);
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
`;

export const featureIconStyles = css`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 3px;
`;
